import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import MentorshipService, {
    MENTOR_POOL_ALREADY_IN_POOL,
    MENTOR_POOL_ENGAGEMENT_LEVELS,
    MENTOR_POOL_EXPERIENCE_BUCKETS,
    MENTOR_POOL_UNKNOWN_USER_NOT_CONFIGURED,
    MentorshipCampusOption,
    MentorshipImportResult,
    MentorshipManualSearchCandidate,
    MentorshipMentorPoolCandidate,
    MentorshipTaxonomyTrack,
    MentorshipWatchlistListQuery,
} from './MentorshipService';

const WATCHLIST_QUADRANTS = [
    { value: 'mentorship_core', label: 'Core' },
    { value: 'mentorship_conversion', label: 'Conversion' },
    { value: 'mentorship_breakthrough', label: 'Breakthrough' },
    { value: 'mentorship_reactivation', label: 'Reactivation' },
];

const toggleValue = (values: string[], value: string) =>
    values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

/** R-QA-01 intake 44 / DEC-P3-025 override: only these upload columns. `student_id` is accepted as a uuid alias by the parser. */
const MENTOR_POOL_SAMPLE_CSV = 'institute_id,uuid,career_tracks\n';

const downloadMentorPoolSampleCsv = () => {
    const blob = new Blob([MENTOR_POOL_SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mentorship_mentor_pool_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const displayCandidateTracks = (
    item: MentorshipMentorPoolCandidate,
    taxonomyTracks: MentorshipTaxonomyTrack[] = []
) =>
    item.tracks
        .map((track) => track.name || taxonomyTracks.find((option) => option.id === track.id)?.name || null)
        .filter((name): name is string => Boolean(name))
        .join(', ') || '—';

const isRawCampusIdText = (text: string, campusId: number) => {
    const trimmed = text.trim();
    if (!trimmed) return true;
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) && asNumber === campusId && String(asNumber) === trimmed;
};

const displayCandidateCampus = (
    item: MentorshipMentorPoolCandidate,
    campuses: MentorshipCampusOption[] = []
) => {
    if (item.campusAmbiguous) return 'Campus unresolved';
    if (item.campusId == null) return '—';
    const campusId = Number(item.campusId);
    if (!Number.isFinite(campusId)) return '—';

    const fromApi = item.campusName == null ? '' : String(item.campusName).trim();
    if (fromApi && !isRawCampusIdText(fromApi, campusId)) return fromApi;

    const fromOptions = campuses
        .find((campus) => Number(campus.value) === campusId)
        ?.label?.trim();
    if (fromOptions && !isRawCampusIdText(fromOptions, campusId)) return fromOptions;

    return '—';
};

const ImportSummary = ({ result }: { result: MentorshipImportResult | null }) => {
    if (!result) return null;
    const inviteFeedback = MentorshipService.formatInvitationDeliveryFeedback(result.invitationDelivery);
    return (
        <div className="mt-3">
            <div className="alert alert-light border mb-2">
                Imported {result.imported.length}. Failed {result.failed.length}.
            </div>
            {inviteFeedback && (
                <div className={MentorshipService.inviteFeedbackAlertClass(inviteFeedback.tone)}>
                    {inviteFeedback.text}
                </div>
            )}
            {result.failed.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Row</th>
                                <th>Identifier</th>
                                <th>Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {result.failed.map((item, index) => (
                                <tr key={`${item.alumniId || item.identifier || index}-${item.reason || ''}`}>
                                    <td>{item.row ?? '—'}</td>
                                    <td>{item.identifier || item.alumniId || '—'}</td>
                                    {/* Readable text — the API returns snake_case reason codes. */}
                                    <td>{MentorshipService.describeUploadFailureReason(item.reason)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

interface SharedImportProps {
    show: boolean;
    cohortId: number | null;
    disabled?: boolean;
    campuses: MentorshipCampusOption[];
    tracks: MentorshipTaxonomyTrack[];
    onClose: () => void;
    onImported: () => void;
}

export const MentorshipSystemIdentifiedModal = ({
    show,
    cohortId,
    disabled,
    campuses,
    tracks,
    onClose,
    onImported,
}: SharedImportProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [candidates, setCandidates] = useState<MentorshipMentorPoolCandidate[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [result, setResult] = useState<MentorshipImportResult | null>(null);

    const importable = useMemo(
        () =>
            candidates.filter(
                (item) => !item.campusAmbiguous && item.campusId != null && item.tracks.some((track) => track.id)
            ),
        [candidates]
    );

    useEffect(() => {
        if (!show || cohortId == null) return;
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');
            setResult(null);
            setSelected([]);
            try {
                const data = await MentorshipService.listSystemIdentifiedCandidates(cohortId);
                if (!cancelled) setCandidates(data.candidates);
            } catch (error) {
                if (!cancelled) {
                    setCandidates([]);
                    setErrorMessage(MentorshipService.getErrorMessage(error));
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [show, cohortId]);

    const toggle = (alumniId: string, allowed: boolean) => {
        if (!allowed) return;
        setSelected((current) => (current.includes(alumniId) ? current.filter((id) => id !== alumniId) : [...current, alumniId]));
    };

    const importSelected = async () => {
        if (cohortId == null || selected.length === 0) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            const imported = await MentorshipService.importSystemIdentifiedCandidates(cohortId, selected);
            setResult(imported);
            if (imported.imported.length > 0) onImported();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>System Identified</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                {isLoading ? (
                    <div className="text-muted">Loading candidates…</div>
                ) : candidates.length === 0 ? (
                    <div className="text-muted">No System Identified candidates for this cohort.</div>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: 420 }}>
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th />
                                    <th>Name</th>
                                    <th>Tracks</th>
                                    <th>Campus</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((item) => {
                                    const allowed = !item.campusAmbiguous && item.campusId != null && item.tracks.length > 0;
                                    return (
                                        <tr key={item.alumniId}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(item.alumniId)}
                                                    disabled={!allowed || disabled}
                                                    onChange={() => toggle(item.alumniId, allowed)}
                                                />
                                            </td>
                                            <td>
                                                {item.alumni.name || '—'}
                                                <div className="small text-muted">{item.alumni.uuid}</div>
                                            </td>
                                            <td>{displayCandidateTracks(item, tracks)}</td>
                                            <td>{displayCandidateCampus(item, campuses)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <ImportSummary result={result} />
            </Modal.Body>
            <Modal.Footer>
                <div className="me-auto small text-muted">{importable.length} ready to import</div>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
                <Button variant="success" disabled={disabled || isSaving || selected.length === 0} onClick={importSelected}>
                    Import Selected
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const MentorshipWatchlistModal = ({
    show,
    cohortId,
    disabled,
    campuses,
    tracks,
    onClose,
    onImported,
}: SharedImportProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [candidates, setCandidates] = useState<MentorshipMentorPoolCandidate[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [result, setResult] = useState<MentorshipImportResult | null>(null);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });
    const [draft, setDraft] = useState<MentorshipWatchlistListQuery>({});
    const [applied, setApplied] = useState<MentorshipWatchlistListQuery>({});

    const load = async (query: MentorshipWatchlistListQuery, nextPage = 1) => {
        if (cohortId == null) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            const data = await MentorshipService.listWatchlistCandidates(cohortId, { ...query, page: nextPage, pageSize: 25 });
            setCandidates(data.candidates);
            setPagination(data.pagination);
            setPage(data.pagination.page);
            setSelected([]);
        } catch (error) {
            setCandidates([]);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!show || cohortId == null) return;
        setDraft({});
        setApplied({});
        setResult(null);
        setInfoMessage('');
        load({}, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, cohortId]);

    const canImport = (item: MentorshipMentorPoolCandidate) =>
        !item.campusAmbiguous && item.campusId != null && item.tracks.some((track) => track.id);

    const importMembers = async (invite: boolean, selectAll = false) => {
        if (cohortId == null) return;
        setIsSaving(true);
        setErrorMessage('');
        setInfoMessage('');
        try {
            const imported = await MentorshipService.importWatchlistCandidates(cohortId, {
                ...applied,
                invite,
                selectAll,
                members: selectAll
                    ? undefined
                    : candidates
                          .filter((item) => selected.includes(item.alumniId) && canImport(item))
                          .map((item) => ({
                              alumniId: item.alumniId,
                              campusId: item.campusId,
                              trackIds: item.tracks.map((track) => track.id).filter((id) => id != null),
                          })),
            });
            setResult(imported);
            if (imported.imported.length > 0) onImported();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Career Guidance Watchlist</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                {infoMessage && <div className="alert alert-info">{infoMessage}</div>}
                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <input
                            className="form-control"
                            placeholder="Search name / email / UUID"
                            value={draft.search || ''}
                            onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            className="form-control"
                            placeholder="Expertise"
                            value={draft.expertise || ''}
                            onChange={(event) => setDraft((current) => ({ ...current, expertise: event.target.value }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            className="form-control"
                            placeholder="Company"
                            value={draft.company || ''}
                            onChange={(event) => setDraft((current) => ({ ...current, company: event.target.value }))}
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            className="form-control"
                            placeholder="Designation"
                            value={draft.designation || ''}
                            onChange={(event) => setDraft((current) => ({ ...current, designation: event.target.value }))}
                        />
                    </div>
                    <div className="col-12">
                        <div className="small text-muted mb-1">Quadrant</div>
                        {WATCHLIST_QUADRANTS.map((item) => (
                            <label key={item.value} className="me-3">
                                <input
                                    type="checkbox"
                                    className="me-1"
                                    checked={(draft.quadrant || []).includes(item.value)}
                                    onChange={() =>
                                        setDraft((current) => ({ ...current, quadrant: toggleValue(current.quadrant || [], item.value) }))
                                    }
                                />
                                {item.label}
                            </label>
                        ))}
                    </div>
                    <div className="col-12">
                        <div className="small text-muted mb-1">Experience</div>
                        {MENTOR_POOL_EXPERIENCE_BUCKETS.map((item) => (
                            <label key={item} className="me-3">
                                <input
                                    type="checkbox"
                                    className="me-1"
                                    checked={(draft.experience || []).includes(item)}
                                    onChange={() =>
                                        setDraft((current) => ({
                                            ...current,
                                            experience: toggleValue(current.experience || [], item),
                                        }))
                                    }
                                />
                                {item}
                            </label>
                        ))}
                    </div>
                    {(['ap', 'mq', 'vl'] as const).map((key) => (
                        <div className="col-md-4" key={key}>
                            <div className="small text-muted mb-1">{key.toUpperCase()}</div>
                            {MENTOR_POOL_ENGAGEMENT_LEVELS.map((level) => (
                                <label key={level} className="me-2">
                                    <input
                                        type="checkbox"
                                        className="me-1"
                                        checked={(draft[key] || []).includes(level)}
                                        onChange={() =>
                                            setDraft((current) => ({
                                                ...current,
                                                [key]: toggleValue(current[key] || [], level),
                                            }))
                                        }
                                    />
                                    {level}
                                </label>
                            ))}
                        </div>
                    ))}
                    <div className="col-12">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                setApplied(draft);
                                load(draft, 1);
                            }}>
                            Apply filters
                        </button>
                    </div>
                </div>
                {isLoading ? (
                    <div className="text-muted">Loading watchlist…</div>
                ) : candidates.length === 0 ? (
                    <div className="text-muted">No Watchlist results for the selected filters.</div>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: 360 }}>
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th />
                                    <th>Name</th>
                                    <th>Company</th>
                                    <th>Tracks</th>
                                    <th>Campus</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map((item) => {
                                    const allowed = canImport(item);
                                    return (
                                        <tr key={item.alumniId}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(item.alumniId)}
                                                    disabled={!allowed || disabled}
                                                    onChange={() =>
                                                        setSelected((current) =>
                                                            current.includes(item.alumniId)
                                                                ? current.filter((id) => id !== item.alumniId)
                                                                : [...current, item.alumniId]
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                {item.alumni.name || '—'}
                                                <div className="small text-muted">{item.alumni.uuid}</div>
                                            </td>
                                            <td>{MentorshipService.displayCurrentRole(item.alumni.company, item.alumni.designation)}</td>
                                            <td>{displayCandidateTracks(item, tracks)}</td>
                                            <td>{displayCandidateCampus(item, campuses)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">
                        Page {pagination.page} of {pagination.totalPages || 1} · {pagination.total} results
                    </div>
                    <div className="btn-group">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={page <= 1 || isLoading}
                            onClick={() => load(applied, page - 1)}>
                            Previous
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={page >= (pagination.totalPages || 1) || isLoading}
                            onClick={() => load(applied, page + 1)}>
                            Next
                        </button>
                    </div>
                </div>
                <ImportSummary result={result} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
                <Button
                    variant="outline-secondary"
                    disabled={disabled || isSaving || pagination.total === 0}
                    onClick={() => importMembers(false, true)}>
                    Select All filtered · Import
                </Button>
                <Button
                    variant="success"
                    disabled={disabled || isSaving || selected.length === 0}
                    onClick={() => importMembers(false, false)}>
                    Import Selected
                </Button>
                <Button
                    variant="outline-success"
                    disabled={disabled || isSaving || selected.length === 0}
                    onClick={() => importMembers(true, false)}>
                    Import & Invite Selected
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const MentorshipFileUploadModal = ({
    show,
    cohortId,
    disabled,
    onClose,
    onImported,
}: SharedImportProps) => {
    const [file, setFile] = useState<File | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [result, setResult] = useState<MentorshipImportResult | null>(null);

    useEffect(() => {
        if (!show) return;
        setFile(null);
        setResult(null);
        setErrorMessage('');
    }, [show]);

    const upload = async () => {
        if (cohortId == null || !file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrorMessage('CSV files only.');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            const imported = await MentorshipService.uploadMentorPoolFile(cohortId, file);
            setResult(imported);
            if (imported.imported.length > 0) onImported();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>File Upload</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                <p className="text-muted mb-2">Upload a CSV mentor list. XLSX/XLS is not supported.</p>
                {/* The sample is header-only, so spell out the format the parser expects. */}
                <ul className="text-muted small ps-3 mb-2">
                    <li>
                        <code>institute_id</code> — required, must be a campus in this cohort
                    </li>
                    <li>
                        <code>uuid</code> — required (an <code>email</code> column works instead)
                    </li>
                    <li>
                        <code>career_tracks</code> — track names from this cohort&apos;s Domain Taxonomy.
                        For more than one, separate with commas inside quotes: <code>&quot;Track A,Track B&quot;</code>
                    </li>
                    {/* Skills come from the alumni profile, so the file no longer carries them. */}
                </ul>
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mb-3"
                    onClick={downloadMentorPoolSampleCsv}>
                    Download sample CSV
                </button>
                <input
                    type="file"
                    className="form-control"
                    accept=".csv,text/csv"
                    disabled={disabled}
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
                <ImportSummary result={result} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
                <Button variant="success" disabled={disabled || isSaving || !file} onClick={upload}>
                    Upload
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const MentorshipManualAddModal = ({
    show,
    cohortId,
    disabled,
    campuses,
    tracks,
    onClose,
    onImported,
}: SharedImportProps) => {
    const [search, setSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [candidates, setCandidates] = useState<MentorshipManualSearchCandidate[]>([]);
    const [selected, setSelected] = useState<MentorshipManualSearchCandidate | null>(null);
    const [campusId, setCampusId] = useState<number | null>(null);
    const [trackIds, setTrackIds] = useState<number[]>([]);
    const [company, setCompany] = useState('');
    const [designation, setDesignation] = useState('');
    const [skillsText, setSkillsText] = useState('');

    useEffect(() => {
        if (!show) return;
        setSearch('');
        setCandidates([]);
        setSelected(null);
        setCampusId(campuses.length === 1 ? campuses[0].value : null);
        setTrackIds([]);
        setCompany('');
        setDesignation('');
        setSkillsText('');
        setErrorMessage('');
        setInfoMessage('');
    }, [show, campuses]);

    const runSearch = async () => {
        if (cohortId == null || !search.trim()) return;
        setIsSearching(true);
        setErrorMessage('');
        setInfoMessage('');
        setSelected(null);
        try {
            const rows = await MentorshipService.searchManualAddCandidates(cohortId, search.trim());
            setCandidates(rows);
            if (rows.length === 0) {
                setInfoMessage(MENTOR_POOL_UNKNOWN_USER_NOT_CONFIGURED);
            }
        } catch (error) {
            setCandidates([]);
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSearching(false);
        }
    };

    const choose = (candidate: MentorshipManualSearchCandidate) => {
        setSelected(candidate);
        setCompany(candidate.company || '');
        setDesignation(candidate.designation || '');
        // Skills come from the alumni profile (skill_code -> domain_data, else other_skill) and are
        // shown read-only; the admin does not type them.
        setSkillsText((candidate.skills || []).join(', '));
        const preferred = candidate.campuses[0]?.campusId ?? (campuses.length === 1 ? campuses[0].value : null);
        setCampusId(preferred);
        if (candidate.alreadyInPool) {
            setErrorMessage(MENTOR_POOL_ALREADY_IN_POOL);
        } else {
            setErrorMessage('');
        }
    };

    const skills = skillsText
        .split(',')
        .map((item) => item.trim())
        .filter((item, index, all) => item !== '' && all.findIndex((value) => value.toLowerCase() === item.toLowerCase()) === index);

    const submit = async () => {
        if (cohortId == null || !selected || selected.alreadyInPool) return;
        if (campusId == null) {
            setErrorMessage('Campus is required.');
            return;
        }
        if (trackIds.length < 1) {
            setErrorMessage('Select at least one career track.');
            return;
        }
        if (!company.trim()) {
            setErrorMessage('Current company is required.');
            return;
        }
        if (!designation.trim()) {
            setErrorMessage('Current designation is required.');
            return;
        }
        if (skills.length < 5) {
            setErrorMessage('Number of skill must be atleast 5');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.addManualMentorPoolMember(cohortId, {
                alumniId: selected.alumniId,
                campusId,
                trackIds,
                currentCompany: company.trim(),
                currentDesignation: designation.trim(),
                skills,
            });
            onImported();
            onClose();
        } catch (error) {
            const message = MentorshipService.getErrorMessage(error);
            if (message === 'semi_verified_creation_not_configured') {
                setErrorMessage(MENTOR_POOL_UNKNOWN_USER_NOT_CONFIGURED);
            } else {
                setErrorMessage(message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Manual Add</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                {infoMessage && <div className="alert alert-info">{infoMessage}</div>}
                <div className="input-group mb-3">
                    <input
                        className="form-control"
                        placeholder="Search name, UUID, or email"
                        value={search}
                        disabled={disabled}
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                runSearch();
                            }
                        }}
                    />
                    <button type="button" className="btn btn-outline-secondary" disabled={disabled || isSearching} onClick={runSearch}>
                        Search
                    </button>
                </div>
                {candidates.length > 0 && (
                    <div className="list-group mb-3" style={{ maxHeight: 240, overflowY: 'scroll' }}>
                        {candidates.map((item) => (
                            <button
                                type="button"
                                key={item.alumniId}
                                className={`list-group-item list-group-item-action ${selected?.alumniId === item.alumniId ? 'active' : ''}`}
                                onClick={() => choose(item)}>
                                {item.name || '—'} · {item.uuid}
                                {item.alreadyInPool ? ` · ${MENTOR_POOL_ALREADY_IN_POOL}` : ''}
                            </button>
                        ))}
                    </div>
                )}
                {selected && !selected.alreadyInPool && (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Campus</label>
                            {/* Campus comes from the alumni profile. Locked when the candidate maps to a
                                single campus; still selectable when they span several. */}
                            <select
                                className="form-select"
                                value={campusId ?? ''}
                                disabled={(selected.campuses || []).length <= 1}
                                onChange={(event) => setCampusId(event.target.value ? Number(event.target.value) : null)}>
                                <option value="">Select campus</option>
                                {campuses.map((campus) => (
                                    <option key={campus.value} value={campus.value}>
                                        {campus.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Current Company</label>
                            <input className="form-control" value={company} readOnly />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Current Designation</label>
                            <input className="form-control" value={designation} readOnly />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Career Tracks</label>
                            <div>
                                {tracks
                                    .filter((track) => track.id != null)
                                    .map((track) => (
                                        <label key={track.id} className="me-3">
                                            <input
                                                type="checkbox"
                                                className="me-1"
                                                checked={trackIds.includes(track.id as number)}
                                                onChange={() =>
                                                    setTrackIds((current) =>
                                                        current.includes(track.id as number)
                                                            ? current.filter((id) => id !== track.id)
                                                            : [...current, track.id as number]
                                                    )
                                                }
                                            />
                                            {track.name}
                                        </label>
                                    ))}
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label">Skills (comma-separated, at least 5)</label>
                            <textarea className="form-control" rows={2} value={skillsText} readOnly />
                            <div className="small text-muted">{skills.length} unique skills</div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
                <Button
                    variant="success"
                    disabled={disabled || isSaving || !selected || selected.alreadyInPool}
                    onClick={submit}>
                    Add to Mentor Pool
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
